import { z } from 'zod';

/**
 * Phase ⑤ JpResearchWorkflow — schémas de la JP analysée.
 *
 * **Classification à 4 valeurs (D46 + révision benchmark 2026-05-01)** :
 * `argument_relation` fusionne le filtre amont et la position argumentative
 * en une seule décision actionnable.
 *
 * Vocabulaire issu du benchmark approche B (50 cas réels, two-pass Grok 4.1)
 * — sémantique action-oriented plus utile au Strategist que la version
 * descriptive d'origine :
 *
 * - `garder` — la règle de l'arrêt s'applique tel quel à notre espèce, JP utilisable directement
 * - `remodeler` — cas voisin, utile à condition d'adapter l'argument autour
 * - `invalider` — la règle contredit ou limite ce que l'argument réclame
 * - `non_pertinent` — pas de rapport (logué en DB, **pas envoyé** au Strategist)
 *
 * Les JP `non_pertinent` n'ont que les 2 champs minimaux (`JpEntryShort`).
 * Les 3 autres ont l'analyse narrative complète (`JpEntry`).
 *
 * Voir hub doc § 4 ⑤ (`docs/architecture/plan-detaille-v2.md`).
 */

// ─────────────────────────────────────────────────────────────────────
// Énumérations
// ─────────────────────────────────────────────────────────────────────

export const ArgumentRelationActiveSchema = z.enum([
  'garder',
  'remodeler',
  'invalider',
]);
export type ArgumentRelationActive = z.infer<typeof ArgumentRelationActiveSchema>;

export const PorteeSchema = z.enum(['generale', 'ancree_cas_espece']);
export type Portee = z.infer<typeof PorteeSchema>;

/**
 * `apport_global` discrétisé (D43) — intègre proximité factuelle ×
 * pondération juridiction (CC=CE > CA > TJ/TCOM). Le LLM mentionne le
 * niveau de juridiction explicitement dans `apport_pour_argument`.
 */
export const ApportGlobalSchema = z.union([
  z.literal(0),                                    // faible
  z.literal(1),                                    // moyen
  z.literal(2),                                    // fort
  z.literal(3),                                    // décisif
]);
export type ApportGlobal = z.infer<typeof ApportGlobalSchema>;

export const GagnantAuDispositifSchema = z.enum([
  'cote_client',
  'cote_adverse',
  'mixte',
]);
export type GagnantAuDispositif = z.infer<typeof GagnantAuDispositifSchema>;

/**
 * Sévérité agrégée du risque associé à un arrêt (V1-bis 2026-05-08).
 *
 * - `none`  : aucun risque détecté — JP utilisable sans réserve.
 * - `minor` : nuance / distinguo (consommateur vs pro, période de validité,
 *   variation procédurale légère). Le rédacteur final doit en tenir compte
 *   mais l'arrêt reste citable côté demandeur.
 * - `major` : passage à risque substantiel — citation sélective requise,
 *   logique inversée détectée, contre-argument adverse présent dans le même
 *   arrêt. Verdict `garder` interdit.
 * - `critical` : arrêt CONTRE-PRODUCTIF — la cour a modéré une clause pénale,
 *   réduit l'indemnité demandée, ou rejeté la qualification cherchée. Citer
 *   cet arrêt côté demandeur reviendrait à fournir l'arme à l'adversaire.
 *   Déclenche un downgrade automatique côté JpAnalyzer : depuis la fusion
 *   du 2026-07-30, `argument_relation` → `non_pertinent` (écarté du pack ;
 *   historiquement → `invalider`).
 */
export const RiskSeveritySchema = z.enum(['none', 'minor', 'major', 'critical']);
export type RiskSeverity = z.infer<typeof RiskSeveritySchema>;

// ─────────────────────────────────────────────────────────────────────
// Provenance d'une décision (TICKET-278)
// ─────────────────────────────────────────────────────────────────────

/**
 * Provenance d'une décision dans le pipeline JP. Pilote le rendu de citation
 * aval (URL publique vérifiable vs mention de provenance interne).
 *
 * - `judilibre` (défaut implicite quand `source` absent — toutes les JpEntry
 *   historiques sont Judilibre) : décision externe → URL courdecassation.fr.
 * - `legifrance-cetat` : décision administrative CETAT → URL legifrance.gouv.fr.
 * - `recherche` : décision **interne** déposée par l'avocat dans un document du
 *   champ « recherche » du dossier. Pas d'URL publique ; citée dans le plan via
 *   une mention de provenance « (Décision extraite du champ recherche du
 *   document 'X') » au lieu de l'URL (cf. `source_document_name`).
 */
export const JpSourceSchema = z.enum(['judilibre', 'legifrance-cetat', 'recherche']);
export type JpSource = z.infer<typeof JpSourceSchema>;

// ─────────────────────────────────────────────────────────────────────
// Identité commune (partagée par JpEntry et JpEntryShort)
// ─────────────────────────────────────────────────────────────────────

const JpIdentityShape = {
  /**
   * ID interne Judilibre (`Decision.id` du gateway). **Identifiant primaire
   * privilégié** dans tout le pipeline — plus stable que ECLI (parfois
   * absent sur les CA récentes) et plus discriminant que pourvoi_number
   * (réutilisable inter-juridictions).
   */
  judilibre_id: z.string().optional(),
  ecli: z.string().optional(),                     // ECLI quand dispo (fallback #1)
  pourvoi_number: z.string().optional(),           // fallback #2 si pas d'ECLI
  juridiction: z.string(),                         // ex: "Cass. com.", "CA Paris"

  /**
   * Libellé COMPLET et déterministe de la juridiction, ville comprise —
   * « Cour d'appel de Versailles », « Tribunal administratif de Grenoble ».
   *
   * WHY (2026-09-10) : `juridiction` est ambigu par construction. Le producteur
   * corpus (`jp-analysis/retrieval/toJpEntry.ts`) y met le CODE brut (`ca`), le
   * producteur legacy (`workflow-generation-jurisprudence-search/JpAnalyzer.ts`)
   * y met déjà un libellé complet, et l'aval s'en sert comme discriminateur de
   * famille (`enrichConsolidatedWithVerbatim.courtFamily`). Ce champ sépare les
   * deux usages : `juridiction` reste la clé technique, `juridiction_complete`
   * porte le libellé que l'acte doit afficher.
   *
   * Sans lui, le code n'avait rien de sûr à opposer au libellé écrit par le LLM
   * agrégateur, qui invente la ville quand elle lui manque — dossier RAFE
   * PRODUCTIONS du 2026-09-08 : 8 cours d'appel sur 9 rendues « CA Paris » alors
   * que les décisions venaient de Versailles, Colmar, Montpellier et Douai.
   *
   * Optionnel : les `jp_pack` persistés avant cette date n'en portent pas, et
   * l'aval retombe alors sur son comportement historique.
   */
  juridiction_complete: z.string().optional(),

  formation: z.string().optional(),                // ex: "AP", "Ch. mixte"

  /**
   * Mention de formation IMPOSÉE PAR LE CODE pour une décision administrative (#1802) :
   * « Ass. », « Sect. », « réf. », ou `null` = n'écrire AUCUNE formation. Calculée depuis
   * `admin_decisions.formation` par `adminCitationFormation`. `enrichConsolidatedWithVerbatim`
   * écrase avec elle la formation renvoyée par le LLM agrégateur — sur les actes droit public,
   * 93 % des « CE Sect. » / « CE Ass. » écrits par le modèle étaient faux.
   *
   * Absente (judiciaire, `jp_pack` persistés avant le 2026-09-29) = pas d'override.
   */
  citation_formation: z.string().nullable().optional(),

  /**
   * Référence complète à recopier dans l'acte, administratif seulement :
   * « CE, Ass., 13 novembre 2013, n° 347704 », « TA Paris, 30 avril 2025, n° 2504120 ».
   * Construite par `formatAdminCitationReference`, jamais par un LLM.
   */
  citation_reference: z.string().optional(),
  date_iso: z.string(),                            // ISO date
  title: z.string(),                               // ex: "Cass. Ch. com., 15 mars 2022, n° 20-12.345"

  /**
   * Provenance de la décision (TICKET-278). Absent = `judilibre` par
   * rétro-compatibilité. Détermine, en aval, si la citation porte une URL
   * publique (`judilibre` / `legifrance-cetat`) ou une mention de provenance
   * interne (`recherche`).
   */
  source: JpSourceSchema.optional(),

  /**
   * Nom exact du document « recherche » du dossier d'où la décision a été
   * extraite, requis sémantiquement quand `source === 'recherche'` (affiché
   * dans la mention de provenance du plan). Absent pour les sources externes,
   * qui portent une URL à la place.
   */
  source_document_name: z.string().optional(),
};

// ─────────────────────────────────────────────────────────────────────
// JpEntryShort — pour les JP `non_pertinent` (loggées DB, hors jp_pack)
// ─────────────────────────────────────────────────────────────────────

export const JpEntryShortSchema = z.object({
  ...JpIdentityShape,
  argument_relation: z.literal('non_pertinent'),
  motif_classification: z.string(),                // 3-5 phrases — pourquoi écartée
}).strict();
export type JpEntryShort = z.infer<typeof JpEntryShortSchema>;

// ─────────────────────────────────────────────────────────────────────
// JpEntry — pour garder / remodeler / invalider
// ─────────────────────────────────────────────────────────────────────

export const JpEntrySchema = z.object({
  // Identité
  ...JpIdentityShape,
  url: z.string().optional(),
  full_content: z.string().optional(),

  // Substance juridique (info brute Judilibre + extraction LLM)
  attendu_cle: z.string(),                         // attendu pertinent vs l'argument
  solution_resume: z.string(),                     // motif central / solution (D33)
  dispositif_summary: z.string(),

  /**
   * V1-bis (2026-05-07) — Synthèse 2-4 phrases NEUTRE et FACTUELLE de la JP
   * écrite par step1 directement (pas une paraphrase aval). Lue verbatim par
   * Sonnet PlanWriter via le markdown amont.
   *
   * WHY : avant ce champ, Sonnet PlanWriter composait sa propre synthèse à
   * partir de `attendu_cle` + `principes_juridiques_extraits` + `apport_concret`,
   * ce qui produisait du drift sémantique cross-layer (paraphrase imprécise,
   * contresens sur dispositifs adverses). En générant la synthèse haut-calibre
   * dès step1 (lecture directe du full_text), on garantit la fidélité sans
   * passer par un agent intermédiaire qui réinterprète.
   *
   * Structure imposée au prompt step1 : SITUATION → PRINCIPE → DÉCISION → POURQUOI.
   * 250-500 chars typiques. Anti-contresens et anti-hallucination strict.
   */
  synthese_pour_avocat: z.string(),
  /**
   * Tous les articles MOBILISÉS par la juridiction (visa formel ET corps de
   * la décision). Fusion D33 — depuis 2026-05 on ne distingue plus visa /
   * cited_articles : un seul champ couvre l'intégralité des articles cités
   * par la cour, qu'ils soient en en-tête ou dans les motifs/dispositif.
   */
  cited_articles: z.array(z.string()),

  // Identification des rôles (transparence du mapping — D17b)
  notre_role_dans_dossier: z.string(),
  role_equivalent_dans_jp: z.string(),
  roles_alignes: z.boolean(),

  /**
   * Nature du dispositif final — chaîne libre (pas un enum). Ex. :
   * "CASSE", "REJETTE", "CONFIRME en partie / INFIRME en partie",
   * "PRONONCE le divorce", "ADMET au passif pour 12 345 €",
   * "FIXE la créance à...", "CONDAMNE à interdiction de gérer 5 ans",
   * "DEBOUTE de toutes les demandes". Le format libre permet de capter
   * les dispositifs mixtes ou les mesures spécifiques (interdiction de
   * gérer, expulsion, restitution, plan de cession arrêté…) que l'ancien
   * enum à 6 valeurs (CASSE/REJETTE/CONFIRME/INFIRME/CONDAMNE/DEBOUTE)
   * ne pouvait pas représenter — bench 2026-05-04 a montré que ce
   * vocabulaire fermé produisait des contresens systématiques sur les
   * dispositifs mixtes ou non-standard.
   */
  dispositif_nature: z.string().min(1),
  gagnant_au_dispositif: GagnantAuDispositifSchema,

  // ─── CLASSIFICATION (D46 — 3 valeurs car JpEntry exclut 'non_pertinent')
  argument_relation: ArgumentRelationActiveSchema,
  motif_classification: z.string(),                // 3-5 phrases

  // ─── SIGNAUX COMPACTS pour le Strategist
  portee: PorteeSchema,                            // D42
  apport_global: ApportGlobalSchema,               // D43

  // ─── PRINCIPES ATTRIBUÉS (V1-bis — remplace les citations littérales)
  /**
   * Prose précise (2-4 phrases) — principes juridiques posés par CET arrêt,
   * avec attribution nominative à la source (« la 1ère chambre civile a posé
   * que… », « l'arrêt confirme… »). Pas de citation littérale entre marqueurs.
   *
   * Pourquoi : la fidélité ne vient pas d'un copy-paste verbatim mais de
   * l'attribution nominative claire à l'arrêt source — ce qui permet à
   * l'audit cross-layer (fidelity judge) de tracer chaque principe utilisé
   * en aval jusqu'à sa source Step1.
   */
  principes_juridiques_extraits: z.string(),

  // ─── TEXTE NARRATIF (cœur de l'analyse Grok reasoning — D40)
  // ⚠️ resume_jp : plus produit depuis l'allègement Step-2 du 2026-07-30 —
  // reste requis pour la read-compat des packs persistés ; les producteurs
  // vivants remplissent '' (convention champ requis sans source).
  resume_jp: z.string(),                           // 3-5 phrases — sujet, faits, solution (legacy)
  apport_pour_argument: z.string(),                // 5-8 phrases — apport juridique ET factuel
  avis_synthese: z.string(),                       // 3-5 phrases — lecture LLM, vigilance/force

  // ─── DÉTECTION DE RISQUE (V1-bis 2026-05-08)
  /**
   * Liste libre de flags décrivant les risques associés à l'arrêt — ex.
   * `'modere_clause_penale_50pct'`, `'logique_inversee_taux'`,
   * `'citation_selective_required'`, `'rejette_di_complementaires'`,
   * `'qualifie_idi_clause_penale_revisable'`. Cardinalité libre, mais
   * le LLM doit éviter les flags ad-hoc — préférer les patterns récurrents.
   *
   * Exposé downstream (Aggregator + FinalPlanWriter) pour permettre une
   * décision éclairée sur la mobilisation de l'arrêt. Vide par défaut.
   */
  risk_flags: z.array(z.string()).default([]),
  /** Cf. `RiskSeveritySchema` ci-dessus pour la sémantique des 4 niveaux. */
  risk_severity: RiskSeveritySchema.default('none'),
  /**
   * Passage textuel de l'arrêt à NE PAS reprendre dans le plan final.
   * Rempli lorsque la cour traite à la fois un point favorable ET un point
   * défavorable au demandeur dans la même décision (ex. modération de clause
   * pénale en marge d'une qualification favorable). L'avocat cite le premier
   * sans exposer le second au juge. Optionnel.
   */
  excerpt_to_avoid: z.string().optional(),
}).strict();
export type JpEntry = z.infer<typeof JpEntrySchema>;

// ─────────────────────────────────────────────────────────────────────
// JpSignals — agrégés par argument, indicatifs (pas prescriptifs) — D45
// ─────────────────────────────────────────────────────────────────────

export const JpSignalsSchema = z.object({
  total: z.number().int().min(0),                  // = |jp_pack|, exclut 'non_pertinent'
  by_relation: z.object({
    garder: z.number().int().min(0),
    remodeler: z.number().int().min(0),
    invalider: z.number().int().min(0),
  }).strict(),
  by_portee: z.object({
    generale: z.number().int().min(0),
    ancree_cas_espece: z.number().int().min(0),
  }).strict(),
  by_apport: z.object({
    faible: z.number().int().min(0),
    moyen: z.number().int().min(0),
    fort: z.number().int().min(0),
    decisif: z.number().int().min(0),
  }).strict(),
  has_jp_principe_invalidante: z.boolean(),        // signal critique : portee='generale' & relation='invalider'
  /**
   * Quality gate (D-bench-2026-05-01) : true si TOUTES les JP retenues sont
   * `invalider` — aucune JP `garder`/`remodeler` trouvée. Le Strategist
   * adapte alors sa stratégie (anticipation contre-argument adverse, ou
   * écarte l'argument).
   */
  all_adverse: z.boolean(),
}).strict();
export type JpSignals = z.infer<typeof JpSignalsSchema>;

// ─────────────────────────────────────────────────────────────────────
// JpPack — output complet de ⑤ par argument
// ─────────────────────────────────────────────────────────────────────

export const JpPackSchema = z.object({
  argument_id: z.string(),
  jp_pack: z.array(JpEntrySchema).max(20),         // plafond 20, ordonné par apport_global desc
  jp_ecartees: z.array(JpEntryShortSchema),        // 'non_pertinent' — DB only
  jp_signals: JpSignalsSchema,
  warnings: z.array(z.string()),
}).strict();
export type JpPack = z.infer<typeof JpPackSchema>;
